import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";

import {
  GIVE_UP_AFTER_MS,
  POLL_MS,
  useDashboardPolling,
} from "./company-dashboard-polling";

export const DASHBOARD_RESILIENCE_PATH = "/api/v1/dashboard/resilience/";

// A suppressed figure is "not enough people", never zero.
export type ScoreTile =
  | { suppressed: true; value: null }
  | { suppressed: false; value: number };

export type GroupScores = {
  index: ScoreTile;
  domains: Record<string, ScoreTile>;
  themes: Record<string, ScoreTile>;
};

export type HalfScores = {
  people: ScoreTile;
  all_staff: GroupScores;
  // Every grouping is always present; a group too small to show is left out.
  segments: Record<
    "location" | "business_unit" | "manager",
    Record<string, GroupScores>
  >;
  strengths: string[];
  focus_areas: string[];
};

// `value` is what the endpoint takes (`2026-H1`), `label` what the page
// shows (`H1 2026`).
export type Period = { value: string; label: string };

// Within the selected half: everyone assessed by its end, and the session
// feedback given during it.
export type Activity = {
  employees: ScoreTile;
  // Absent from a Platform older than the web bundle.
  assessments?: ScoreTile;
  less_stressed_percent: ScoreTile;
  gained_skills_percent: ScoreTile;
  recommendation: ScoreTile;
};

// The company Index for every finished half from the first up to the selected
// one, each over everyone's latest assessment by that half's end. Withheld
// entirely while that is fewer than two halves.
export type Trend = {
  suppressed: boolean;
  basis: string;
  // `period` is Platform's English label, "H1 2026".
  points: { period: string; index: ScoreTile; people: ScoreTile }[];
};

// People whose latest assessment by the half's end is not their first.
export type Repeat = {
  repeat_takers: ScoreTile;
  // Their average Index change from first to latest assessment.
  index_change: ScoreTile;
  // Up to two domain keys, biggest rise first.
  domains_rose_most: string[];
};

export type ResilienceReport = {
  company_name: string;
  reused: boolean;
  period: string;
  previous_period: string;
  scores: {
    selected: HalfScores;
    previous: HalfScores;
    // All staff's move on each score since the previous half, hidden wherever
    // either half's figure is.
    change: GroupScores;
  };
  activity: Activity;
  trend: Trend;
  repeat: Repeat;
  // The half these figures are for, as a `value`. `periods` is listed at the
  // moment of answering, so across midnight it can lead with a newer half.
  period_value: string;
  periods: Period[];
};

type ResilienceResponse =
  | ({ status: "ready" } & ResilienceReport)
  | {
      status: "running";
      handle: string;
      period_value: string;
      periods: Period[];
    };

export type ResilienceState =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; report: ResilienceReport };

type ResilienceContextValue = {
  status: ResilienceState;
  periods: Period[];
  // The half shown or being asked for, as its `value`; null until Platform
  // has named the latest one.
  selected: string | null;
  // From the last half that loaded, so it stays put while another loads.
  companyName: string | null;
  open: () => void;
  select: (period: string) => void;
  retry: () => void;
};

const ResilienceContext = createContext<ResilienceContextValue | null>(null);

const startPath = (period: string | null) =>
  period === null
    ? DASHBOARD_RESILIENCE_PATH
    : `${DASHBOARD_RESILIENCE_PATH}?period=${encodeURIComponent(period)}`;

/**
 * The Employee Resilience page's figures, one finished half-year at a time.
 *
 * Nothing is asked until the tab opens (`open`), since every first view
 * starts Athena queries. The first ask names no period, so Platform picks the
 * latest finished half; a 202 is checked again with its handle until the
 * figures are ready. Each half's figures are kept for as long as the
 * dashboard is open, so going back to one is instant and asks nothing.
 */
export function DashboardResilienceProvider({
  children,
  pollMs = POLL_MS,
  giveUpAfterMs = GIVE_UP_AFTER_MS,
}: {
  children: ReactNode;
  // Overridden only by tests, so polling and the give-up run without the wait.
  pollMs?: number;
  giveUpAfterMs?: number;
}) {
  const [status, setStatus] = useState<ResilienceState>({ state: "idle" });
  const [periods, setPeriods] = useState<Period[]>([]);
  const cache = useRef(new Map<string, ResilienceReport>());
  const [selected, setSelected] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const requested = useRef<string | null>(null);
  const opened = useRef(false);
  const { request, cancel } = useDashboardPolling({
    path: DASHBOARD_RESILIENCE_PATH,
    name: "resilience",
    pollMs,
    giveUpAfterMs,
  });

  const load = useCallback(
    (period: string | null) => {
      cancel();
      requested.current = period;
      setSelected(period);

      const cached = period === null ? undefined : cache.current.get(period);
      if (cached) {
        setStatus({ state: "ready", report: cached });
        return;
      }

      setStatus({ state: "loading" });
      // Asked without a period, Platform names the half it started; every
      // later ask, a restart included, names that half.
      let asked = period;

      request<ResilienceResponse>({
        startPath: () => startPath(asked),
        onAnswer: (response) => {
          setPeriods(response.periods);
          if (typeof response.period_value !== "string") {
            throw new Error("Resilience answered without naming its half");
          }
          if (asked === null) {
            asked = response.period_value;
            requested.current = asked;
            setSelected(asked);
          }
          if (response.status === "ready") {
            cache.current.set(asked, response);
            setCompanyName(response.company_name);
            setStatus({ state: "ready", report: response });
          }
        },
        onError: () => setStatus({ state: "error" }),
      });
    },
    [request, cancel]
  );

  // A remounted tab (Fast Refresh, StrictMode) has to be able to ask again.
  useEffect(
    () => () => {
      opened.current = false;
    },
    []
  );

  const open = useCallback(() => {
    if (opened.current) return;
    opened.current = true;
    load(null);
  }, [load]);

  const value = useMemo(
    () => ({
      status,
      periods,
      selected,
      companyName,
      open,
      select: (period: string) => load(period),
      retry: () => load(requested.current),
    }),
    [status, periods, selected, companyName, open, load]
  );

  return (
    <ResilienceContext.Provider value={value}>
      {children}
    </ResilienceContext.Provider>
  );
}

export function useDashboardResilience(): ResilienceContextValue {
  const value = useContext(ResilienceContext);
  if (value === null) {
    throw new Error(
      "useDashboardResilience needs a DashboardResilienceProvider above it"
    );
  }
  return value;
}

/**
 * Whether the half has nothing to show: the all-staff Index and every domain
 * are hidden, because nobody was assessed or too few people were to show any.
 */
export function nothingScored(scores: HalfScores): boolean {
  const all = scores.all_staff;
  return (
    all.index.suppressed &&
    Object.values(all.domains).every((tile) => tile.suppressed)
  );
}
